import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import { PrimaryButton } from 'components/buttons/primary-button';
import { SecondaryButton } from 'components/buttons/secondary-button';
import LocalizedLink from 'components/localized-link';
import { LogoNewDesign } from 'components/logo-new-design';
import { MobileNav } from 'components/mobile-nav';
import { NavToggler } from 'components/nav-toggler';

import { PAGE_LINKS } from 'ts/constants/page-links';

import { HeaderNav } from './components/nav';
import s from './header-new-design.module.scss';
import type { IHeaderInteractor } from './interactor';

export interface IProps {
  interactor: IHeaderInteractor;
}
export const HeaderRouter: React.FC<IProps> = ({ interactor }) => {
  const { t } = useTranslation();
  const renderActionButtons = useCallback(() => {
    if (interactor.isAuthorized) {
      return (
        <>
          <PrimaryButton
            onClick={interactor.onDashboard}
            className={classNames(s.dashboardButton, 'font-[700]')}
          >
            <div>{t('header.my_account')}</div>
          </PrimaryButton>
          <SecondaryButton
            onClick={interactor.onLogout}
            className={classNames(s.logoutButton, 'font-[700]')}
          >
            {t('header.logout')}
          </SecondaryButton>
        </>
      );
    }

    return (
      <SecondaryButton
        onClick={interactor.onLogin}
        className={classNames(s.logoutButton, 'font-[700]')}
      >
        {t('header.login')}
      </SecondaryButton>
    );
  }, [interactor.isAuthorized, interactor.onDashboard, interactor.onLogin, interactor.onLogout, t]);

  const renderMobileNav = () => {
    if (interactor.mobileNav.isVisible) {
      return (
        <MobileNav
          isAuthorized={interactor.isAuthorized}
          isContactUsShown={interactor.isMainPage}
          onLogin={interactor.onMobileLogin}
          toggleMobileNav={interactor.mobileNav.toggle}
          onDashboard={interactor.onMobileDashboard}
          onLogout={interactor.onLogout}
          onMobileContactUs={interactor.onMobileContactUs}
          isMainPage={interactor.isMainPage}
        />
      );
    }

    return null;
  };

  return (
    <div className='h-[64px]'>
      <div className={s.header}>
        <div className='container'>
          <div className={s.wrapper}>
            <div className={s.logoNav}>
              <div
                role='button'
                className={s.logoWrapper}
                onClick={interactor.clickOnLogo}
              >
                <LocalizedLink href={PAGE_LINKS.MAIN}>
                  <LogoNewDesign />
                </LocalizedLink>
              </div>
              <div className={s.desktopNav}>
                <HeaderNav interactor={interactor} />
              </div>
              <NavToggler
                isOpen={interactor.mobileNav.isVisible}
                toggle={interactor.mobileNav.toggle}
              />
              {renderMobileNav()}
            </div>
            <div className={s.desktopNav}>{renderActionButtons()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
